import { CheckCircle2, Circle, Rocket } from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card.jsx";
import { Progress } from "@/shared/ui/progress.jsx";
import { LoadingState, EmptyState } from "@/shared/ui/spinner.jsx";
import { formatDate } from "@/shared/lib/format.js";
import { cn } from "@/shared/lib/cn.js";
import { StatCard, ClientStatusBadge } from "./components/MasterUi.jsx";
import { useMasterOnboarding } from "./use-master.js";

/**
 * Implantação dos clientes.
 *
 * O progresso é derivado do que existe de fato no banco de cada clínica:
 * usuário cadastrado, WhatsApp conectado, jornada criada, leads no funil.
 * Antes era um número na coluna `onboarding_progress` digitado à mão, que
 * envelhecia sem ninguém perceber.
 */
export function OnboardingPage() {
  const { data: clinicas = [], isLoading } = useMasterOnboarding();

  if (isLoading) {
    return (
      <MasterShell title="Onboarding e Setup" subtitle="Acompanhamento da implantação">
        <LoadingState message="Calculando o progresso..." />
      </MasterShell>
    );
  }

  const concluidos = clinicas.filter((c) => c.progress === 100).length;
  const iniciados = clinicas.filter((c) => c.progress > 0 && c.progress < 100).length;
  const parados = clinicas.filter((c) => c.progress === 0).length;

  return (
    <MasterShell title="Onboarding e Setup" subtitle="Acompanhamento da implantação dos clientes">
      <div className="space-y-6 p-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="Implantação concluída" value={concluidos} icon={CheckCircle2} accent="success" />
          <StatCard label="Em andamento" value={iniciados} icon={Rocket} accent="warning" />
          <StatCard label="Sem nenhum passo" value={parados} icon={Circle} accent="destructive" />
        </div>

        {clinicas.length === 0 ? (
          <EmptyState icon={Rocket} title="Nenhum cliente em implantação" />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {clinicas.map((clinica) => (
              <Card key={clinica.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="truncate font-display text-base">
                        {clinica.name}
                      </CardTitle>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Cliente desde {formatDate(clinica.createdAt)}
                      </p>
                    </div>
                    <ClientStatusBadge status={clinica.status} />
                  </div>

                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Progresso</span>
                      <span className="font-semibold">{clinica.progress}%</span>
                    </div>
                    <Progress
                      value={clinica.progress}
                      indicatorClassName={
                        clinica.progress === 100
                          ? "bg-success"
                          : clinica.progress === 0
                            ? "bg-destructive"
                            : "bg-primary"
                      }
                    />
                  </div>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-2">
                    {clinica.steps.map((etapa) => (
                      <li key={etapa.id} className="flex items-center gap-2 text-sm">
                        {etapa.done ? (
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                        ) : (
                          <Circle className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                        )}
                        <span className={cn(etapa.done ? "" : "text-muted-foreground")}>
                          {etapa.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MasterShell>
  );
}
